import { useState, useEffect } from 'react';
import { App } from '@capacitor/app';
import { isNative } from 'utils/platform.utils';

/**
 * Hook to get application version information for both native and web platforms
 * @returns {Object} { version, build, appName, appId, environment, loading, error }
 */
const useAppVersion = () => {
  const [versionInfo, setVersionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getAppVersion = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let info;
        
        if (isNative()) {
          // Use Capacitor App API for native platforms
          const appInfo = await App.getInfo();
          info = {
            version: appInfo.version,
            build: appInfo.build,
            appName: appInfo.name,
            appId: appInfo.id,
            environment: process.env.NODE_ENV || 'production'
          };
        } else {
          // Use package.json and environment variables for web platform
          info = {
            version: process.env.REACT_APP_VERSION || '1.0.0',
            build: process.env.REACT_APP_BUILD || 'web-build',
            appName: process.env.REACT_APP_NAME || 'Inxits Customer App',
            appId: process.env.REACT_APP_ID || 'com.inxits.customer.web',
            environment: process.env.NODE_ENV || 'development'
          };
        }
        
        setVersionInfo(info);
      } catch (err) {
        console.error('Failed to get app version:', err);
        setError(err.message || 'Failed to get app version');
        
        // Fallback for web platform
        if (!isNative()) {
          setVersionInfo({
            version: '1.0.0',
            build: 'web-build',
            appName: 'Inxits Customer App',
            appId: 'com.inxits.customer.web',
            environment: process.env.NODE_ENV || 'development'
          });
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    getAppVersion();
  }, []);

  return { 
    ...versionInfo, 
    loading, 
    error 
  };
};

export default useAppVersion;
